import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
} from '@material-ui/core';
import { FlightFinancialReport } from 'app/services/financial-reports.services';
import { t } from 'i18next';

interface Props {
  open: boolean;
  loading: boolean;
  error?: string | null;
  data?: FlightFinancialReport | null;
  onClose: () => void;
}

const Label: React.FC = ({ children }) => (
  <Typography variant="body2" style={{ fontWeight: 600 }}>
    {children}
  </Typography>
);

const Value: React.FC = ({ children }) => (
  <Typography variant="body2">{children}</Typography>
);

const TotalTurnoverModal: React.FC<Props> = ({
  open,
  loading,
  error,
  data,
  onClose,
}) => {
  const totalTurnover =
    data ? data.brutFlightTotal + data.refundableTaxes : 0;

  const loadFactor =
    data && data.totalSeats
      ? ((data.occupiedSeats / data.totalSeats) * 100).toFixed(1)
      : '0.0';

  const yieldPerSeat =
    data && data.totalSeats
      ? (data.netTotal / data.totalSeats).toFixed(2)
      : '0.00';

  return (

<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
  <DialogTitle>
    {t("app.main.schedules.totalTurnover.dialogTitle")}
  </DialogTitle>

  <DialogContent dividers>
    {loading && <Typography>{t("app.main.schedules.totalTurnover.loading")}</Typography>}

    {!loading && error && (
      <Typography color="error">
        {t("app.main.schedules.totalTurnover.error")}
      </Typography>
    )}

    {!loading && !error && data && (
      <>
        <Typography variant="subtitle2" gutterBottom>
          {data.flightNumber} | {data.route}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Label>{t("app.main.schedules.totalTurnover.totalSeats")}</Label>
            <Value>{data.totalSeats}</Value>
          </Grid>

          <Grid item xs={6}>
            <Label>{t("app.main.schedules.totalTurnover.occupiedSeats")}</Label>
            <Value>{data.occupiedSeats}</Value>
          </Grid>

          <Grid item xs={6}>
            <Label>{t("app.main.schedules.totalTurnover.emptySeats")}</Label>
            <Value>{data.emptySeats}</Value>
          </Grid>

          <Grid item xs={6}>
            <Label>{t("app.main.schedules.totalTurnover.noShowSeats")}</Label>
            <Value>{data.noShowSeats}</Value>
          </Grid>

          <Grid item xs={6}>
            <Label>{t("app.main.schedules.totalTurnover.brutFlightTotal")}</Label>
            <Value>{data.brutFlightTotal} {data.currencyCode}</Value>
          </Grid>

          <Grid item xs={6}>
            <Label>{t("app.main.schedules.totalTurnover.refundableTaxes")}</Label>
            <Value>{data.refundableTaxes} {data.currencyCode}</Value>
          </Grid>

          <Grid item xs={6}>
            <Label>{t("app.main.schedules.totalTurnover.totalTurnover")}</Label>
            <Value>{totalTurnover} {data.currencyCode}</Value>
          </Grid>

          <Grid item xs={6}>
            <Label>{t("app.main.schedules.totalTurnover.netTotal")}</Label>
            <Value>{data.netTotal} {data.currencyCode}</Value>
          </Grid>

          <Grid item xs={6}>
            <Label>{t("app.main.schedules.totalTurnover.averagePerTraveler")}</Label>
            <Value>{data.averagePerTraveler} {data.currencyCode}</Value>
          </Grid>

          <Grid item xs={6}>
            <Label>{t("app.main.schedules.totalTurnover.averagePerSeat")}</Label>
            <Value>{data.averagePerSeat} {data.currencyCode}</Value>
          </Grid>

          <Grid item xs={6}>
            <Label>{t("app.main.schedules.totalTurnover.totalAirportTax")}</Label>
            <Value>{data.totalAirportTax} {data.currencyCode}</Value>
          </Grid>

          <Grid item xs={6}>
            <Label>{t("app.main.schedules.totalTurnover.loadFactor")}</Label>
            <Value>{loadFactor}%</Value>
          </Grid>

          <Grid item xs={6}>
            <Label>{t("app.main.schedules.totalTurnover.yieldPerSeat")}</Label>
            <Value>{yieldPerSeat} {data.currencyCode}</Value>
          </Grid>
        </Grid>
      </>
    )}
  </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TotalTurnoverModal;
